






import { computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref, watch } from "@vue/composition-api";
import SignaturePad, { Options, PointGroup } from "signature_pad";
import { DigitalSignStatus, GetSecondArg } from "./types";
import { DEFAULT_OPTIONS, TRANSPARENT_PNG } from "./utils";

export default defineComponent({
  name: "SignaturePadWrapper",
  props: {
    width: { type: String, default: "100%" },
    height: { type: String, default: "100%" },
    customStyle: { type: Object as PropType<Record<string, string>>, default: () => ({}) },
    options: { type: Object as PropType<Options>, default: () => ({}) },
  },

  setup(props) {
    const signaturePadCanvas = ref<HTMLCanvasElement | null>(null);
    const signaturePad = ref<SignaturePad | null>(null);
    const signatureData = ref(TRANSPARENT_PNG);

    const wrapperStyle = computed(() => ({
      width: props.width,
      height: props.height,
      ...props.customStyle,
    }));

    const canvasStyle = computed(() => ({
      width: props.width,
      height: props.height,
    }));

    const updateOptions = (nextOptions: Options): void => {
      if (!signaturePad.value) return;

      for (const option of Object.keys(nextOptions)) {
        if (Object.prototype.hasOwnProperty.call(signaturePad.value, option)) {
          ((signaturePad.value as unknown) as Record<string, unknown>)[option] = nextOptions[option as keyof Options];
        }
      }
    };

    const resizeCanvas = (): void => {
      if (!signaturePadCanvas.value || !signaturePad.value) return;

      const canvas = signaturePadCanvas.value;
      const data = signaturePad.value.toData();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);

      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")?.scale(ratio, ratio);

      signaturePad.value.clear();
      signatureData.value = TRANSPARENT_PNG;
      signaturePad.value.fromData(data);
    };

    const saveSignature = (encoderOptions?: number): DigitalSignStatus => {
      const status: DigitalSignStatus = { isEmpty: false, data: undefined };

      if (!signaturePad.value || signaturePad.value.isEmpty()) {
        return { ...status, isEmpty: true };
      }

      signatureData.value = signaturePad.value.toDataURL("image/png", encoderOptions);

      return { ...status, data: signatureData.value };
    };

    const renderSignIntoPad = async (
      data: string,
      options: GetSecondArg<SignaturePad["fromDataURL"]> = {}
    ): Promise<void> => {
      await signaturePad.value?.fromDataURL(data, options);
    };

    const toData = (): PointGroup[] => signaturePad.value?.toData() ?? [];

    const clearSignature = (): void => {
      signaturePad.value?.clear();
      signatureData.value = TRANSPARENT_PNG;
    };

    watch(() => props.options, updateOptions);

    onMounted(() => {
      if (!signaturePadCanvas.value) return;

      signaturePad.value = new SignaturePad(signaturePadCanvas.value, { ...DEFAULT_OPTIONS, ...props.options });

      window.addEventListener("resize", resizeCanvas, false);
      resizeCanvas();
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", resizeCanvas, false);
    });

    return {
      signaturePadCanvas,
      wrapperStyle,
      canvasStyle,
      clearSignature,
      renderSignIntoPad,
      resizeCanvas,
      saveSignature,
      toData,
    };
  },
});
